import type { Item, Category, ItemType } from '@/types';
import { isUrlSync, normalizeUrlSync } from '@/services/llmAnalysis';

/**
 * Normalize a string for search comparison
 * Converts to lowercase and removes extra whitespace
 */
function normalizeString(str: string): string {
  return str.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Check if a string contains the search query
 */
function matchesQuery(text: string | null | undefined, query: string): boolean {
  if (!text) return false;
  return normalizeString(text).includes(query);
}

/**
 * Get searchable text fields for an item based on its type
 */
function getSearchableFields(item: Item): string[] {
  const commonFields = [item.title, item.description, ...item.tags];

  switch (item.type) {
    case 'link':
      return [...commonFields, item.url, item.siteName, item.personalNotes].filter(
        (f): f is string => f !== null && f !== undefined
      );
    case 'podcast':
      return [...commonFields, item.url, item.podcastName, item.personalNotes].filter(
        (f): f is string => f !== null && f !== undefined
      );
    case 'note':
    case 'idea':
      return [...commonFields, item.plainTextPreview];
    case 'file':
      return [...commonFields, item.originalFileName, item.personalNotes];
    case 'task':
      return commonFields;
    default:
      return commonFields;
  }
}

/**
 * Search items by query string.
 * Matches against title, description, tags, and type-specific fields.
 *
 * @param items - Array of items to search
 * @param query - Search query string
 * @returns Filtered array of items matching the query
 *
 * @example
 * searchItems(items, 'react') // Returns items with "react" in title, description, or tags
 */
export function searchItems<T extends Item>(items: T[], query: string): T[] {
  if (!query.trim()) {
    return items;
  }

  const normalizedQuery = normalizeString(query);

  return items.filter((item) => {
    const searchableFields = getSearchableFields(item);
    return searchableFields.some((field) => matchesQuery(field, normalizedQuery));
  });
}

/**
 * Filter items by category
 *
 * @param items - Array of items to filter
 * @param category - Category to filter by
 * @returns Filtered array of items
 */
export function filterByCategory<T extends Item>(items: T[], category: Category): T[] {
  switch (category) {
    case 'all':
      return items;
    case 'inbox':
      return items.filter((item) => item.tags.includes('inbox'));
    case 'favorites':
      return items.filter((item) => item.isFavorite);
    default:
      // Filter by item type
      return items.filter((item) => item.type === category);
  }
}

/**
 * Filter items by a specific tag
 *
 * @param items - Array of items to filter
 * @param tag - Tag to filter by
 * @returns Filtered array of items
 */
export function filterByTag<T extends Item>(items: T[], tag: string): T[] {
  const normalizedTag = normalizeString(tag);
  return items.filter((item) => item.tags.some((t) => normalizeString(t) === normalizedTag));
}

/**
 * Combined filter: apply category, search, and optional tag filter
 *
 * @param items - Array of items to filter
 * @param options - Filter options
 * @returns Filtered array of items
 */
export function filterItems<T extends Item>(
  items: T[],
  options: {
    category?: Category | undefined;
    query?: string | undefined;
    tag?: string | undefined;
  }
): T[] {
  let result = items;

  if (options.category) {
    result = filterByCategory(result, options.category);
  }

  if (options.query) {
    result = searchItems(result, options.query);
  }

  if (options.tag) {
    result = filterByTag(result, options.tag);
  }

  return result;
}

/**
 * Sort items by updated date (newest first)
 *
 * @param items - Array of items to sort
 * @returns Sorted array of items
 */
export function sortByUpdated<T extends Item>(items: T[]): T[] {
  return [...items].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

/**
 * Sort items by created date (newest first)
 *
 * @param items - Array of items to sort
 * @returns Sorted array of items
 */
export function sortByCreated<T extends Item>(items: T[]): T[] {
  return [...items].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

/**
 * Get all unique tags from a list of items
 *
 * @param items - Array of items
 * @returns Array of unique tag names
 */
export function getAllTags(items: Item[]): string[] {
  const tagSet = new Set<string>();
  items.forEach((item) => {
    item.tags.forEach((tag) => tagSet.add(tag));
  });
  return Array.from(tagSet).sort();
}

/**
 * Count items by category
 *
 * @param items - Array of all items
 * @returns Object with counts for each category
 */
export function countByCategory(items: Item[]): Record<Category, number> {
  const counts: Record<Category, number> = {
    all: items.length,
    inbox: 0,
    link: 0,
    podcast: 0,
    note: 0,
    idea: 0,
    file: 0,
    task: 0,
    favorites: 0,
  };

  items.forEach((item) => {
    // Count by type
    counts[item.type as ItemType]++;

    // Count inbox items
    if (item.tags.includes('inbox')) {
      counts.inbox++;
    }

    // Count favorites
    if (item.isFavorite) {
      counts.favorites++;
    }
  });

  return counts;
}

/**
 * Detect if a string is a URL.
 * Uses LLM-powered analysis service with fallback to simple heuristics.
 *
 * @param input - String to check
 * @returns True if the string appears to be a URL
 */
export function isUrl(input: string): boolean {
  return isUrlSync(input);
}

/**
 * Normalize a URL (add https:// if missing).
 * Uses LLM-powered analysis service with fallback to simple heuristics.
 *
 * @param url - URL string to normalize
 * @returns Normalized URL with protocol
 */
export function normalizeUrl(url: string): string {
  return normalizeUrlSync(url);
}

/**
 * Strip HTML tags from a string to get plain text
 *
 * @param html - HTML string
 * @returns Plain text without HTML tags
 */
export function stripHtml(html: string): string {
  // Create a temporary element to parse HTML
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent ?? tmp.innerText ?? '';
}

/**
 * Create a plain text preview from HTML content
 *
 * @param html - HTML content
 * @param maxLength - Maximum length of preview (default 200)
 * @returns Plain text preview
 */
export function createPlainTextPreview(html: string, maxLength = 200): string {
  const plainText = stripHtml(html).trim().replace(/\s+/g, ' ');
  if (plainText.length <= maxLength) {
    return plainText;
  }
  return plainText.slice(0, maxLength).trim() + '...';
}
